import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Sales, Masters, Files, apiErrorMessage } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { VEHICLE_MODELS } from "../lib/constants";
import { toast } from "sonner";
import {
  Check, ChevronLeft, ChevronRight, Save, Send, User2, Users as UsersIcon,
  Bike, Wallet, Truck, Shield, FileBadge, Banknote, Calculator, FolderUp, Plus, Trash2, ShoppingBag,
} from "lucide-react";
import { cn } from "../lib/utils";
import { computeFinanceSummary, PRICING_V2 } from "../lib/finance";
import SaleUploads from "../components/SaleUploads";
import ReleaseCelebration from "../components/ReleaseCelebration";
import PaymentReceiptsEditor from "../components/PaymentReceiptsEditor";
import { toTitleCase } from "../lib/textCase";

const STEPS = [
  { id: 1, name: "DSE", icon: User2 },
  { id: 2, name: "Customer", icon: UsersIcon },
  { id: 3, name: "Finance", icon: Wallet },
  { id: 4, name: "Insurance", icon: Shield },
  { id: 5, name: "Registration", icon: FileBadge },
  { id: 6, name: "Vehicle", icon: Bike },
  { id: 7, name: "VAS", icon: Truck },
  { id: 8, name: "Accessories", icon: ShoppingBag },
  { id: 9, name: "Payment", icon: Calculator },
  { id: 10, name: "Receipts", icon: Calculator },
  { id: 11, name: "Uploads", icon: FolderUp },
];

const RELATIONS = ["Father", "Mother", "Spouse", "Son", "Daughter", "Brother", "Sister", "Other"];

const empty = {
  location_id: "",
  enquiry: { enquiry_number: "", enquiry_source_id: "", enquiry_sub_source_id: "", booking_date: "" },
  customer: {
    name: "", gst_no: "", phone: "", alt_phone: "", email: "", dob: "", aadhaar: "",
    address: "", at_po: "", city: "", district: "", pin_code: "", state: "Odisha",
  },
  vehicle: { model: "", color: "", color_code: "", vin: "", engine_no: "" },
  finance: { buy_mode: "cash", finance_company_id: "", hypothecation: null, dealer_disbursement_amount: "" },
  insurance: { type: "purchased", plan_type: "", nominee_name: "", relation: "", nominee_age: "" },
  registration: { regn_type_id: "", rto_office: "", choice_no: "" },
  vas: { pp_opted: null, amc_opted: null, rsa_opted: null,
         pp_id: "", amc_id: "", rsa_id: "" },
  accessory_items: [],
  payment_receipts: [],
  delivery: { promised_date: "", promised_time: "", accessories: "", notes: "" },
  payment: {
    exshowroom: "", registration: "", hypothecation: "", insurance: "",
    pp: "", amc: "", rsa: "", accessories: "", admin_incidental: "",
    down_payment: "", dp_accessories: "",
    discounts: { total: { value: "", mode: "abs" } },
  },
};

const REGEX = {
  // 10 digits (any leading digit allowed).
  mobile: /^\d{10}$/,
  aadhaar: /^\d{4}-\d{4}-\d{4}$/,
  // 17 alphanumeric chars (uppercase, all letters allowed).
  vin: /^[A-Z0-9]{17}$/,
  // Exactly 12 alphanumeric chars — Engine Number spec (Feb-2026).
  engine: /^[A-Z0-9]{12}$/,
};

function formatAadhaar(input) {
  const digits = (input || "").replace(/\D/g, "").slice(0, 12);
  return digits.replace(/(\d{4})(?=\d)/g, "$1-");
}

export default function SalesForm() {
  const { activeUser, locations, users } = useApp();
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saleId, setSaleId] = useState(routeId || null);
  const [loadingDraft, setLoadingDraft] = useState(!!routeId);
  const [released, setReleased] = useState(null); // { id, sr_number } when released
  const [postRelease, setPostRelease] = useState(false); // true when editing a released/in-progress sale
  const [postReleaseSrNumber, setPostReleaseSrNumber] = useState(null);
  // Billing-lock flags fetched once per sale (post-release only). Sales / SM
  // are blocked from modifying accessories / VAS once Accounts has billed or
  // BO has marked the VAS plan Completed. Admin bypasses every lock.
  const [lockState, setLockState] = useState({
    delivered: false,
    accessories_billed: false,
    vas_billed: { pp: false, amc: false, rsa: false },
    can_override: false,
  });

  // Master data
  const [masters, setMasters] = useState({
    enquiry_source: [], enquiry_sub_source: [], finance_company: [],
    regn_type: [], vas_protection_plus: [], vas_amc: [], vas_rsa: [],
    vehicle_model: [], state: [], district: [],
  });

  useEffect(() => {
    (async () => {
      const types = ["enquiry_source", "enquiry_sub_source", "finance_company", "regn_type", "vas_protection_plus", "vas_amc", "vas_rsa", "vehicle_model", "state", "district"];
      const out = {};
      // Only active masters are surfaced in the Sales Form dropdowns —
      // Admin can retire an entry without breaking historical sales.
      await Promise.all(types.map(async (t) => { out[t] = await Masters.list({ type: t, active_only: true }); }));
      setMasters(out);
    })();
  }, []);

  const dseUsers = useMemo(() => users.filter((u) => u.role === "DSE"), [users]);
  const allowedLocs = useMemo(
    () => locations.filter((l) =>
      !activeUser?.location_ids?.length || activeUser.location_ids.includes(l.id),
    ),
    [locations, activeUser],
  );

  const [form, setForm] = useState(() => ({
    ...empty,
    location_id: allowedLocs[0]?.id || "",
    dse_id: activeUser?.role === "DSE" ? activeUser.id : (dseUsers[0]?.id || activeUser?.id || ""),
  }));

  // Load existing draft when editing
  useEffect(() => {
    if (!routeId) return;
    (async () => {
      try {
        const sale = await Sales.get(routeId);
        // Stringify scalar payment fields ONLY. Nested objects (e.g. `discounts`)
        // must be preserved as-is so the per-component discount UI keeps working.
        const stringy = (obj) =>
          Object.fromEntries(
            Object.entries(obj || {}).map(([k, v]) => {
              if (v == null) return [k, ""];
              if (typeof v === "object") return [k, v];
              return [k, String(v)];
            }),
          );
        setForm({
          location_id: sale.location_id || "",
          dse_id: sale.dse_id || "",
          enquiry: { ...empty.enquiry, ...(sale.enquiry || {}) },
          customer: { ...empty.customer, ...(sale.customer || {}) },
          vehicle: { ...empty.vehicle, ...(sale.vehicle || {}) },
          finance: { ...empty.finance, ...(sale.finance || {}) },
          insurance: {
            ...empty.insurance,
            ...(sale.insurance || {}),
            nominee_age: sale.insurance?.nominee_age != null ? String(sale.insurance.nominee_age) : "",
          },
          registration: { ...empty.registration, ...(sale.registration || {}) },
          vas: { ...empty.vas, ...(sale.vas || {}) },
          delivery: { ...empty.delivery, ...(sale.delivery || {}) },
          payment: { ...empty.payment, ...stringy(sale.payment) },
          accessory_items: (sale.accessory_items || []).map((a, i) => ({
            name: a.name || "",
            price: a.price != null ? String(a.price) : "",
            _uid: `acc-${Date.now()}-${i}`,
          })),
          payment_receipts: (sale.payment_receipts || []).map((r) => ({
            ...r,
            amount: r.amount != null ? String(r.amount) : "",
          })),
        });
        setSaleId(sale.id);
        if (sale.status !== "draft") {
          setPostRelease(true);
          setPostReleaseSrNumber(sale.sr_number || null);
          // Fetch lock state in parallel — used to grey-out billed fields and
          // surface the "Only Admin can modify" banner.
          try {
            const ls = await Sales.lockState(sale.id);
            setLockState(ls);
            if (ls.delivered) {
              toast.error("Delivered sales cannot be edited. The record is frozen.");
              navigate(`/sales/${sale.id}`, { replace: true });
              return;
            }
          } catch (_) { /* fail-open: locks default to false */ }
        }
      } catch (e) {
        toast.error("Failed to load draft");
      } finally {
        setLoadingDraft(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  const setField = (group, key, val) =>
    setForm((f) => ({ ...f, [group]: { ...f[group], [key]: val } }));

  const setTop = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  // Auto-fill VAS prices into payment when selecting a plan
  const onVasChange = (slot, paymentKey, masterType, val) => {
    setForm((f) => {
      const next = { ...f, vas: { ...f.vas, [slot]: val } };
      const plan = (masters[masterType] || []).find((m) => m.id === val);
      if (plan?.price != null) {
        next.payment = { ...next.payment, [paymentKey]: String(plan.price) };
      }
      return next;
    });
  };

  // Toggle customer opt-in per VAS plan. We keep the plan_id and payment
  // amount intact when toggling to "No" — the row is visually hidden and
  // excluded from all totals via the `*_opted` flag downstream. Preserving
  // the value means the user can safely toggle No → Yes without losing
  // their previously-selected plan and price (Feb-2026 spec fix).
  const onVasOptedChange = (optKey, _slotKey, _paymentKey, opted) => {
    setForm((f) => ({ ...f, vas: { ...f.vas, [optKey]: opted } }));
  };

  // Filter sub-sources based on selected source
  const subSources = useMemo(() => {
    const sid = form.enquiry.enquiry_source_id;
    return masters.enquiry_sub_source.filter((s) => !sid || s.parent_id === sid);
  }, [masters, form.enquiry.enquiry_source_id]);

  // Payment totals — one global discount line at the bottom of On-Road Breakup
  // (stored under payment.discounts.total = {value, mode}).
  const totalGross = useMemo(() => {
    const p = form.payment;
    const v = form.vas || {};
    const vasSkip = { pp: v.pp_opted === false, amc: v.amc_opted === false, rsa: v.rsa_opted === false };
    const keys = ["exshowroom", "registration", "hypothecation", "insurance", "pp", "amc", "rsa", "accessories", "admin_incidental"];
    return keys.reduce((s, k) => s + (vasSkip[k] ? 0 : (Number(p[k]) || 0)), 0);
  }, [form.payment, form.vas]);

  const totalPayment = useMemo(
    () => applyDiscountFn(totalGross, form.payment.discounts?.total),
    [totalGross, form.payment.discounts],
  );

  const customerPayable = useMemo(() => {
    const p = form.payment;
    return (Number(p.down_payment) || 0) + (Number(p.dp_accessories) || 0);
  }, [form.payment]);

  // v2 pricing preview — every new sales form gets stamped pricing_version="v2"
  // on the server, so the live preview should reflect the new formula. Edits
  // to a legacy v1 sale continue to render with the old logic (see below).
  const isV2Form = (form.pricing_version || PRICING_V2) === PRICING_V2;
  const v2Summary = useMemo(
    () => computeFinanceSummary({ ...form, pricing_version: PRICING_V2 }),
    [form],
  );

  // Accessories total = simple sum of item prices (no accessory-side discount).
  const accessoriesTotal = useMemo(
    () => (form.accessory_items || []).reduce((s, a) => s + (Number(a.price) || 0), 0),
    [form.accessory_items],
  );

  // Auto-sync accessories total into payment.accessories and payment.dp_accessories
  useEffect(() => {
    setForm((f) => {
      const desired = accessoriesTotal ? String(accessoriesTotal) : "";
      if (
        f.payment.accessories === desired &&
        f.payment.dp_accessories === desired
      ) return f;
      return {
        ...f,
        payment: { ...f.payment, accessories: desired, dp_accessories: desired },
      };
    });
  }, [accessoriesTotal]);

  // Accessory item helpers
  const addAccessory = () =>
    setForm((f) => ({
      ...f,
      accessory_items: [
        ...(f.accessory_items || []),
        { name: "", price: "", _uid: `acc-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` },
      ],
    }));
  const updateAccessory = (idx, key, val) =>
    setForm((f) => ({
      ...f,
      accessory_items: f.accessory_items.map((a, i) => (i === idx ? { ...a, [key]: val } : a)),
    }));
  const removeAccessory = (idx) =>
    setForm((f) => ({ ...f, accessory_items: f.accessory_items.filter((_, i) => i !== idx) }));

  // ---- Validation ----
  const validateStep = (s) => {
    const e = form.enquiry, c = form.customer, fin = form.finance, ins = form.insurance,
          reg = form.registration, v = form.vehicle, d = form.delivery;
    if (s === 1) {
      if (!form.location_id) return "Please select a location";
      if (!form.dse_id) return "DSE is required";
      if (!e.enquiry_number?.trim()) return "Enquiry Number is required";
      if (!e.enquiry_source_id) return "Enquiry Source is required";
      if (!e.enquiry_sub_source_id) return "Enquiry Sub Source is required";
      if (!d.promised_date) return "Promised Delivery Date is required";
      if (!d.promised_time) return "Promised Delivery Time is required";
    }
    if (s === 2) {
      if (!c.name?.trim()) return "Customer name is required";
      if (!REGEX.mobile.test(c.phone || "")) return "WhatsApp Number must be exactly 10 digits";
      if (!REGEX.mobile.test(c.alt_phone || "")) return "Alt Mobile must be a valid 10-digit number";
      if (!c.email?.trim()) return "Email is required";
      if (!c.dob) return "DOB is required";
      if (!REGEX.aadhaar.test(c.aadhaar || "")) return "Aadhaar must be in xxxx-xxxx-xxxx format";
      if (!c.address?.trim()) return "Address is required";
      if (!c.at_po?.trim()) return "At/Po is required";
      if (!c.city?.trim()) return "City/Town is required";
      if (!c.district?.trim()) return "District is required";
      if (!c.pin_code?.trim()) return "Pin Code is required";
      if (!c.state?.trim()) return "State is required";
    }
    if (s === 3) {
      if (!fin.buy_mode) return "Buy Mode is required";
      if (fin.buy_mode === "finance" && !fin.finance_company_id) return "Finance Company is required";
      if (fin.buy_mode === "finance" && (fin.hypothecation !== true && fin.hypothecation !== false))
        return "Please confirm Hypothecation (Yes / No)";
    }
    if (s === 4) {
      if (!ins.type) return "Insurance type is required";
      if (ins.type === "purchased") {
        if (!ins.plan_type) return "Plan Type is required (1 OD + 5 TP / 5 OD + 5 TP)";
        if (!ins.nominee_name?.trim()) return "Insurance Nominee is required";
        if (!ins.relation) return "Relation is required";
        if (!ins.nominee_age) return "Nominee age is required";
      }
    }
    if (s === 5) {
      if (!reg.regn_type_id) return "Regn Type is required";
      if (!reg.rto_office?.trim()) return "RTO Office is required";
      if (!reg.choice_no?.trim()) return "Choice No is required (use 'NA' if none)";
    }
    if (s === 6) {
      if (!v.model) return "Vehicle Model is required";
      if (!v.color_code?.trim()) return "Color Code is required";
      const vin = (v.vin || "").trim();
      const eng = (v.engine_no || "").trim();
      // Both-or-neither rule: leaving both blank at draft/release is allowed
      // — PDI fills them later. But once ONE is entered, the other becomes
      // mandatory too. This prevents half-populated records from moving
      // downstream (spec, Feb-2026).
      if (vin && !eng)
        return "Engine Number is required when VIN is entered. Please enter both together.";
      if (eng && !vin)
        return "VIN is required when Engine Number is entered. Please enter both together.";
      if (vin && !REGEX.vin.test(vin)) return "VIN must contain exactly 17 alphanumeric characters";
      if (eng && !REGEX.engine.test(eng)) return "Engine Number must be exactly 12 alphanumeric characters.";
    }
    if (s === 9) {
      const pmt = form.payment || {};
      const dp = Number(pmt.down_payment) || 0;
      if (dp > 0 && dp > totalPayment + 0.5) {
        return "Downpayment cannot exceed Final Vehicle Price";
      }
      // DDA lives on the Payment tab for Finance sales (v2 pricing).
      if (form.finance?.buy_mode === "finance") {
        const dda = Number(form.finance.dealer_disbursement_amount);
        if (!form.finance.dealer_disbursement_amount || !Number.isFinite(dda) || dda <= 0)
          return "Dealer Disbursement Amount is required for Finance sales";
      }
    }
    if (s === 7) {
      const vas = form.vas || {};
      if (vas.pp_opted === null) return "Please confirm Protection Plus opt-in (Yes / No)";
      if (vas.amc_opted === null) return "Please confirm AMC opt-in (Yes / No)";
      if (vas.rsa_opted === null) return "Please confirm RSA opt-in (Yes / No)";
      if (vas.pp_opted === true && !vas.pp_id) return "Select a Protection Plus plan";
      if (vas.amc_opted === true && !vas.amc_id) return "Select an AMC plan";
      if (vas.rsa_opted === true && !vas.rsa_id) return "Select an RSA plan";
    }
    return null;
  };

  const next = () => {
    // Skip validation in post-release mode so users can navigate freely to
    // operational fields (e.g. fix VAS opt-in) without having to retroactively
    // satisfy fields that pre-dated newer requirements.
    if (!postRelease) {
      const err = validateStep(step);
      if (err) return toast.error(err);
    }
    setStep((s) => Math.min(STEPS.length, s + 1));
  };
  const prev = () => setStep((s) => Math.max(1, s - 1));

  const buildPayload = (status) => {
    const numeric = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const p = form.payment;
    // Coerce scalar amounts via `numeric()` but keep the nested `discounts`
    // object intact — otherwise `Number({...})` produces NaN which serialises
    // to `null` and wipes the saved discount on every PATCH.
    const paymentOut = Object.fromEntries(
      Object.entries(p)
        .filter(([k]) => k !== "discounts")
        .map(([k, v]) => [k, numeric(v)]),
    );
    const discTotal = p.discounts?.total;
    paymentOut.discounts = discTotal && (discTotal.value !== "" && discTotal.value != null)
      ? { total: { value: Number(discTotal.value) || 0, mode: discTotal.mode || "abs" } }
      : null;
    return {
      location_id: form.location_id,
      dse_id: form.dse_id,
      enquiry: form.enquiry,
      customer: form.customer,
      vehicle: form.vehicle,
      finance: {
        ...form.finance,
        // Empty string "" would fail Pydantic Optional[float] validation.
        // Cash sales carry a null DDA; finance sales carry a numeric value.
        dealer_disbursement_amount:
          form.finance.buy_mode === "finance"
            ? numeric(form.finance.dealer_disbursement_amount)
            : null,
      },
      insurance: { ...form.insurance, nominee_age: numeric(form.insurance.nominee_age) },
      registration: form.registration,
      vas: form.vas,
      delivery: form.delivery,
      payment: paymentOut,
      accessory_items: (form.accessory_items || [])
        .filter((a) => a.name?.trim())
        .map((a) => ({ name: a.name.trim(), price: Number(a.price) || 0 })),
      payment_receipts: (form.payment_receipts || [])
        .filter((r) => (r.mode && (Number(r.amount) || 0) > 0))
        .map((r) => ({
          id: r.id && !String(r.id).startsWith("tmp-") ? r.id : undefined,
          receipt_no: (r.receipt_no || "").trim() || null,
          mode: r.mode,
          amount: Number(r.amount) || 0,
          received_on: r.received_on || null,
          notes: (r.notes || "").trim() || null,
        })),
      status,
    };
  };

  const saveDraft = async ({ stay = false } = {}) => {
    if (!form.location_id) return toast.error("Please select a location first");
    if (!form.dse_id) return toast.error("DSE is required");
    // Hard-stop Downpayment > Final Price even when saving a draft / post-release edit.
    const dp = Number(form.payment?.down_payment) || 0;
    if (dp > totalPayment + 0.5) {
      setStep(9);
      return toast.error("Downpayment cannot exceed Final Vehicle Price");
    }
    setSaving(true);
    try {
      let sale;
      if (saleId) {
        const { status: _s, location_id, dse_id, ...rest } = buildPayload("draft");
        sale = await Sales.update(saleId, { ...rest, location_id });
        toast.success(postRelease ? "Changes saved" : "Draft saved", {
          description: postRelease
            ? "Your edits have been logged in the Activity Timeline."
            : "Your changes are safely stored. Keep going.",
          duration: 2200,
        });
      } else {
        sale = await Sales.create(buildPayload("draft"));
        setSaleId(sale.id);
        toast.success("Draft saved · uploads enabled", {
          description: "You can now upload Aadhar, Passport Photo and other documents.",
          duration: 3000,
        });
      }
      if (!stay) navigate(`/sales/${sale.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const release = async () => {
    // Hard-stop Downpayment > Final Price.
    const dp = Number(form.payment?.down_payment) || 0;
    if (dp > totalPayment + 0.5) {
      setStep(9);
      return toast.error("Downpayment cannot exceed Final Vehicle Price");
    }
    for (let i = 1; i <= STEPS.length - 1; i++) {
      // skip optional steps for required validation: Accessories (8), Payment (9), Receipts (10)
      if (i === 8 || i === 9 || i === 10) continue;
      const e = validateStep(i);
      if (e) { setStep(i); return toast.error(e); }
    }
    // Required uploads check (Aadhar + Passport Size Photo)
    if (!saleId) {
      setStep(11);
      return toast.error("Save draft first, then upload Aadhar + Passport Photo before releasing");
    }
    try {
      const files = await Files.list(saleId);
      const missing = [];
      if (!files.some((f) => f.type === "aadhar")) missing.push("Aadhar Card");
      if (!files.some((f) => f.type === "passport_photo")) missing.push("Passport Size Photo");
      if (missing.length) {
        setStep(11);
        return toast.error(`Required upload missing: ${missing.join(", ")}`);
      }
    } catch (e) {
      return toast.error("Could not verify uploaded documents");
    }

    setSaving(true);
    try {
      let sale;
      if (saleId) {
        const { status: _s, ...rest } = buildPayload("draft");
        await Sales.update(saleId, rest);
        sale = await Sales.submit(saleId);
      } else {
        sale = await Sales.create(buildPayload("submitted"));
      }
      setReleased({ id: sale.id, sr_number: sale.sr_number });
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to release");
    } finally {
      setSaving(false);
    }
  };

  if (loadingDraft) {
    return <div className="py-10 text-center text-sm text-slate-500">Loading draft…</div>;
  }

  const isEditing = !!routeId;
  const isAdmin = activeUser?.role === "Admin";
  // ── Post-release lock matrix ───────────────────────────────────────────
  // Whitelist of input dot-paths still editable after release. Anything not
  // listed here is rendered read-only with a tooltip. Backend re-enforces
  // the same rule in ``_sanitize_post_release_update``.
  //
  // Admin override: after release, an Admin (and only an Admin) can also fix
  // VIN / Engine No — every other role keeps those fields locked.
  const POST_RELEASE_EDITABLE = postRelease ? new Set([
    "enquiry.enquiry_number",
    "delivery.promised_date",
    "delivery.promised_time",
    "delivery.notes",
    "finance.buy_mode",
    "finance.finance_company_id",
    "finance.hypothecation",
    // Insurance — every field on the step
    "insurance.type", "insurance.plan_type", "insurance.nominee_name",
    "insurance.relation", "insurance.nominee_age",
    // Registration — every field on the step
    "registration.regn_type_id", "registration.rto_office", "registration.choice_no",
    // VAS / Accessories / Payment / Receipts are handled by their own components
    "vas.pp_opted", "vas.pp_id", "vas.amc_opted", "vas.amc_id",
    "vas.rsa_opted", "vas.rsa_id",
    "accessory_items", "accessory_discount",
    "payment", "payment_receipts",
    // Admin-only post-release overrides
    ...(isAdmin ? ["vehicle.vin", "vehicle.engine_no"] : []),
  ]) : null;
  const isLocked = (path) => !!POST_RELEASE_EDITABLE && !POST_RELEASE_EDITABLE.has(path);
  const lockProps = (path) => {
    if (!isLocked(path)) return {};
    return {
      readOnly: true,
      disabled: true,
      className: "bg-slate-100 text-slate-500 cursor-not-allowed",
      title: "Field locked after Sales Release",
    };
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6" data-testid="sales-form-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
            {postRelease ? `Edit Sale · ${postReleaseSrNumber || ""}` : isEditing ? "Edit Draft" : "New Sales Record"}
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            {postRelease ? "Edit Released Sale" : isEditing ? "Edit Sale Draft" : "Create New Sale"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {postRelease
              ? "Operational fields can be updated post-release. All edits are recorded in the Activity Timeline."
              : isEditing
              ? "Update fields and Save Draft, or Release when ready."
              : "11-step capture · save draft to enable document uploads."}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => saveDraft({ stay: true })}
          disabled={saving}
          className="gap-1.5"
          data-testid="save-draft-top-btn"
        >
          <Save className="h-4 w-4" /> {postRelease ? "Save Changes" : "Save Draft"}
        </Button>
      </div>

      {postRelease && (
        <div
          className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          data-testid="post-release-banner"
        >
          <span className="mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-200 text-amber-900">!</span>
          <div className="space-y-0.5">
            <div className="font-semibold">Post-release edit mode</div>
            <div className="text-xs text-amber-800">
              Editable: Enquiry No · Promised Delivery Date/Time · Buy Mode (+ Finance Co. &amp; Hypothecation if Finance) · Insurance · Registration · VAS · Accessories · Payments &amp; Receipts.
              Locked: Customer details, Vehicle (Model / Color), DSE, Location, SR No, Enquiry Source / Sub Source, Booking Date.
              {isAdmin && <span className="font-semibold"> Admin can also correct VIN &amp; Engine No.</span>}
              {" "}Every change is captured in the audit trail.
            </div>
          </div>
        </div>
      )}

      <Stepper step={step} setStep={setStep} postRelease={postRelease} />

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-display text-xl">
            {step}. {STEPS[step - 1].name} Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {step === 1 && (
            <Grid>
              <Field label="Location *">
                <Select value={form.location_id} onValueChange={(v) => setTop("location_id", v)} disabled={isLocked("location_id")}>
                  <SelectTrigger data-testid="form-location" title={isLocked("location_id") ? "Field locked after Sales Release" : undefined}><SelectValue placeholder="Select location" /></SelectTrigger>
                  <SelectContent>
                    {allowedLocs.map((l) => (
                      <SelectItem key={l.id} value={l.id}>{l.name} ({l.code})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="DSE *">
                <Select value={form.dse_id} onValueChange={(v) => setTop("dse_id", v)} disabled={isLocked("dse_id")}>
                  <SelectTrigger data-testid="form-dse" title={isLocked("dse_id") ? "Field locked after Sales Release" : undefined}><SelectValue placeholder="Select DSE" /></SelectTrigger>
                  <SelectContent>
                    {(dseUsers.length ? dseUsers : users).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Enquiry Number *">
                <Input value={form.enquiry.enquiry_number} onChange={(e) => setField("enquiry", "enquiry_number", e.target.value)} data-testid="form-enq-number" />
              </Field>
              <Field label="Booking Date">
                <Input type="date" value={form.enquiry.booking_date} onChange={(e) => setField("enquiry", "booking_date", e.target.value)} data-testid="form-booking-date" {...lockProps("enquiry.booking_date")} />
              </Field>
              <Field label="Enquiry Source *">
                <Select
                  value={form.enquiry.enquiry_source_id}
                  onValueChange={(v) => {
                    setField("enquiry", "enquiry_source_id", v);
                    setField("enquiry", "enquiry_sub_source_id", ""); // reset sub
                  }}
                  disabled={isLocked("enquiry.enquiry_source_id")}
                >
                  <SelectTrigger data-testid="form-enq-source" title={isLocked("enquiry.enquiry_source_id") ? "Field locked after Sales Release" : undefined}><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    {masters.enquiry_source.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-500">Add masters in Admin first</div>
                    ) : masters.enquiry_source.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Enquiry Sub Source *">
                <Select
                  value={form.enquiry.enquiry_sub_source_id}
                  onValueChange={(v) => setField("enquiry", "enquiry_sub_source_id", v)}
                  disabled={!form.enquiry.enquiry_source_id || isLocked("enquiry.enquiry_sub_source_id")}
                >
                  <SelectTrigger data-testid="form-enq-sub-source" title={isLocked("enquiry.enquiry_sub_source_id") ? "Field locked after Sales Release" : undefined}>
                    <SelectValue placeholder={form.enquiry.enquiry_source_id ? "Select sub source" : "Select source first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {subSources.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-500">No sub sources for this source</div>
                    ) : subSources.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Promised Delivery Date *">
                <Input type="date" value={form.delivery.promised_date} onChange={(e) => setField("delivery", "promised_date", e.target.value)} data-testid="form-promised-date" />
              </Field>
              <Field label="Promised Delivery Time *">
                <Input type="time" value={form.delivery.promised_time} onChange={(e) => setField("delivery", "promised_time", e.target.value)} data-testid="form-promised-time" />
              </Field>
            </Grid>
          )}

          {step === 2 && (
            <Grid>
              <Field label="Name *">
                <Input
                  value={form.customer.name}
                  onChange={(e) => setField("customer", "name", e.target.value)}
                  onBlur={(e) => {
                    const v = toTitleCase(e.target.value);
                    if (v !== form.customer.name) setField("customer", "name", v);
                  }}
                  data-testid="form-customer-name"
                  {...lockProps("customer.name")} />
              </Field>
              <Field label="GST No">
                <Input value={form.customer.gst_no} onChange={(e) => setField("customer", "gst_no", e.target.value)} data-testid="form-customer-gst" {...lockProps("customer.gst_no")} />
              </Field>
              <Field label="WhatsApp Number *" hint="10 digits · used for customer communication & automations">
                <Input
                  inputMode="numeric"
                  maxLength={10}
                  value={form.customer.phone}
                  onChange={(e) => setField("customer", "phone", e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter WhatsApp Number"
                  data-testid="form-customer-phone"
                  {...lockProps("customer.phone")}
                />
              </Field>
              <Field label="Alt Mobile Number *">
                <Input
                  inputMode="numeric"
                  maxLength={10}
                  value={form.customer.alt_phone}
                  onChange={(e) => setField("customer", "alt_phone", e.target.value.replace(/\D/g, ""))}
                  data-testid="form-customer-alt-phone"
                  {...lockProps("customer.alt_phone")}
                />
              </Field>
              <Field label="Email *">
                <Input type="email" value={form.customer.email} onChange={(e) => setField("customer", "email", e.target.value)} data-testid="form-customer-email" {...lockProps("customer.email")} />
              </Field>
              <Field label="DOB *">
                <Input type="date" value={form.customer.dob} onChange={(e) => setField("customer", "dob", e.target.value)} data-testid="form-customer-dob" {...lockProps("customer.dob")} />
              </Field>
              <Field label="Aadhaar Card Number *" hint="Format: xxxx-xxxx-xxxx">
                <Input
                  value={form.customer.aadhaar}
                  onChange={(e) => setField("customer", "aadhaar", formatAadhaar(e.target.value))}
                  maxLength={14}
                  placeholder="0000-0000-0000"
                  data-testid="form-customer-aadhaar"
                  {...lockProps("customer.aadhaar")}
                />
              </Field>
              <Field label="State *">
                <MasterSelect
                  value={form.customer.state}
                  options={masters.state}
                  onChange={(v) => setField("customer", "state", v)}
                  placeholder="Select State"
                  testid="form-customer-state"
                  disabled={lockProps("customer.state").readOnly}
                />
              </Field>
              <Field label="Address *" className="md:col-span-2">
                <Textarea rows={2} value={form.customer.address} onChange={(e) => setField("customer", "address", e.target.value)} data-testid="form-customer-address" {...lockProps("customer.address")} />
              </Field>
              <Field label="At/Po *">
                <Input value={form.customer.at_po} onChange={(e) => setField("customer", "at_po", e.target.value)} data-testid="form-customer-atpo" {...lockProps("customer.at_po")} />
              </Field>
              <Field label="City/Town *">
                <Input value={form.customer.city} onChange={(e) => setField("customer", "city", e.target.value)} data-testid="form-customer-city" {...lockProps("customer.city")} />
              </Field>
              <Field label="District *">
                <MasterSelect
                  value={form.customer.district}
                  options={masters.district}
                  onChange={(v) => setField("customer", "district", v)}
                  placeholder="Select District"
                  testid="form-customer-district"
                  disabled={lockProps("customer.district").readOnly}
                />
              </Field>
              <Field label="Pin Code *">
                <Input
                  inputMode="numeric"
                  maxLength={6}
                  value={form.customer.pin_code}
                  onChange={(e) => setField("customer", "pin_code", e.target.value.replace(/\D/g, ""))}
                  data-testid="form-customer-pin"
                  {...lockProps("customer.pin_code")}
                />
              </Field>
            </Grid>
          )}

          {step === 3 && (
            <Grid>
              <Field label="Buy Mode *">
                <Select value={form.finance.buy_mode} onValueChange={(v) => {
                  // v2 pricing rule: clear the DDA cell when switching to Cash so a
                  // stale finance value never leaks into the receivable calculation.
                  setForm((f) => ({
                    ...f,
                    finance: {
                      ...f.finance,
                      buy_mode: v,
                      ...(v === "cash" ? { dealer_disbursement_amount: "" } : {}),
                    },
                  }));
                }}>
                  <SelectTrigger data-testid="form-buy-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {form.finance.buy_mode === "finance" && (
                <Field label="Finance Company *">
                  <Select value={form.finance.finance_company_id} onValueChange={(v) => setField("finance", "finance_company_id", v)}>
                    <SelectTrigger data-testid="form-finance-company"><SelectValue placeholder="Select finance company" /></SelectTrigger>
                    <SelectContent>
                      {masters.finance_company.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-slate-500">Add Finance Companies in Admin → Masters</div>
                      ) : masters.finance_company.map((m) => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              {form.finance.buy_mode === "finance" && (
                <Field label="Hypothecation *" hint="Is the vehicle being hypothecated to the financier?">
                  <Select
                    value={
                      form.finance.hypothecation === true ? "yes"
                      : form.finance.hypothecation === false ? "no"
                      : ""
                    }
                    onValueChange={(v) => {
                      const next = v === "yes" ? true : v === "no" ? false : null;
                      setForm((f) => {
                        const updated = { ...f, finance: { ...f.finance, hypothecation: next } };
                        // Clear hypothecation amount automatically when set to No.
                        if (next === false) updated.payment = { ...f.payment, hypothecation: "" };
                        return updated;
                      });
                    }}
                  >
                    <SelectTrigger data-testid="form-hypothecation"><SelectValue placeholder="Yes / No" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              )}
            </Grid>
          )}

          {step === 4 && (
            <Grid>
              <Field label="Insurance *">
                <Select value={form.insurance.type} onValueChange={(v) => setField("insurance", "type", v)}>
                  <SelectTrigger data-testid="form-insurance-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="purchased">Purchased (from us)</SelectItem>
                    <SelectItem value="customer_self">Customer Self</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {form.insurance.type === "purchased" && (
                <>
                  <Field label="Plan Type *" hint="Choose the insurance package">
                    <Select value={form.insurance.plan_type || ""}
                      onValueChange={(v) => setField("insurance", "plan_type", v)}>
                      <SelectTrigger data-testid="form-insurance-plan-type">
                        <SelectValue placeholder="Select plan" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1+5">1 OD + 5 TP</SelectItem>
                        <SelectItem value="5+5">5 OD + 5 TP</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Insurance Nominee *">
                    <Input value={form.insurance.nominee_name} onChange={(e) => setField("insurance", "nominee_name", e.target.value)} data-testid="form-nominee-name" />
                  </Field>
                  <Field label="Relation *">
                    <Select value={form.insurance.relation} onValueChange={(v) => setField("insurance", "relation", v)}>
                      <SelectTrigger data-testid="form-nominee-relation"><SelectValue placeholder="Select relation" /></SelectTrigger>
                      <SelectContent>
                        {RELATIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Nominee Age *">
                    <Input
                      type="number" min="0" max="120"
                      value={form.insurance.nominee_age}
                      onChange={(e) => setField("insurance", "nominee_age", e.target.value)}
                      data-testid="form-nominee-age"
                    />
                  </Field>
                </>
              )}
            </Grid>
          )}

          {step === 5 && (
            <Grid>
              <Field label="Regn Type *">
                <Select value={form.registration.regn_type_id} onValueChange={(v) => setField("registration", "regn_type_id", v)}>
                  <SelectTrigger data-testid="form-regn-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent>
                    {masters.regn_type.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-500">Add Regn Types in Admin → Masters</div>
                    ) : masters.regn_type.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.code ? `${m.code} — ${m.name}` : m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="RTO Office *">
                <Input value={form.registration.rto_office} onChange={(e) => setField("registration", "rto_office", e.target.value)} placeholder="e.g. KORAPUT 10/30" data-testid="form-rto-office" />
              </Field>
              <Field label="Choice No *" hint="Enter 'NA' if none">
                <Input value={form.registration.choice_no} onChange={(e) => setField("registration", "choice_no", e.target.value)} data-testid="form-choice-no" />
              </Field>
            </Grid>
          )}

          {step === 6 && (
            <VehicleStep form={form} setField={setField} setForm={setForm} masters={masters} isLocked={isLocked} />
          )}

          {step === 7 && (
            <div className="space-y-5" data-testid="vas-step">
              <p className="text-xs text-slate-500">
                These plans are optional. For each one, confirm whether the customer
                has opted in. Choosing <strong>No</strong> means no task will be created
                for that plan after invoice generation.
              </p>
              <VasOptRow
                label="Protection Plus"
                testid="pp"
                opted={form.vas.pp_opted}
                onOptedChange={(v) => onVasOptedChange("pp_opted", "pp_id", "pp", v)}
                planId={form.vas.pp_id}
                onPlanChange={(v) => onVasChange("pp_id", "pp", "vas_protection_plus", v)}
                plans={masters.vas_protection_plus}
                locked={lockState.vas_billed?.pp && !lockState.can_override}
                lockMessage="Protection Plus VAS already billed. Only Admin can modify."
              />
              <VasOptRow
                label="AMC"
                testid="amc"
                opted={form.vas.amc_opted}
                onOptedChange={(v) => onVasOptedChange("amc_opted", "amc_id", "amc", v)}
                planId={form.vas.amc_id}
                onPlanChange={(v) => onVasChange("amc_id", "amc", "vas_amc", v)}
                plans={masters.vas_amc}
                locked={lockState.vas_billed?.amc && !lockState.can_override}
                lockMessage="AMC VAS already billed. Only Admin can modify."
              />
              <VasOptRow
                label="RSA"
                testid="rsa"
                opted={form.vas.rsa_opted}
                onOptedChange={(v) => onVasOptedChange("rsa_opted", "rsa_id", "rsa", v)}
                planId={form.vas.rsa_id}
                onPlanChange={(v) => onVasChange("rsa_id", "rsa", "vas_rsa", v)}
                plans={masters.vas_rsa}
                locked={lockState.vas_billed?.rsa && !lockState.can_override}
                lockMessage="RSA VAS already billed. Only Admin can modify."
              />
            </div>
          )}

          {step === 8 && (
            <AccessoriesEditor
              items={form.accessory_items || []}
              total={accessoriesTotal}
              onAdd={addAccessory}
              onUpdate={updateAccessory}
              onRemove={removeAccessory}
              locked={lockState.accessories_billed && !lockState.can_override}
              lockMessage="Accessories already billed or fulfilled by Spares. Only Admin can modify."
            />
          )}

          {step === 9 && (
            <PaymentStep form={form} setField={setField} setForm={setForm}
              totalPayment={totalPayment} customerPayable={customerPayable}
              isV2Form={isV2Form} v2Summary={v2Summary} />
          )}

          {step === 10 && (
            <PaymentReceiptsEditor
              value={form.payment_receipts || []}
              onChange={(rows) => setForm((f) => ({ ...f, payment_receipts: rows }))}
              cap={isV2Form ? v2Summary.receivable
                : (form.finance.buy_mode === "finance" ? customerPayable : totalPayment)}
              buyMode={form.finance.buy_mode}
              testid="form-receipts"
            />
          )}

          {step === 11 && (
            <div className="space-y-3">
              {!saleId && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
                  <strong>Save the draft first</strong> to enable uploads. Click <em>Save Draft</em> below — you’ll stay on this step and can then upload required documents.
                </div>
              )}
              <SaleUploads
                saleId={saleId}
                uploadedBy={form.dse_id}
              />
            </div>
          )}

          {/* Footer */}
          <div className="flex flex-col gap-3 border-t border-border pt-5 sm:flex-row sm:justify-between">
            <Button variant="outline" onClick={prev} disabled={step === 1} className="gap-1" data-testid="form-back-btn">
              <ChevronLeft className="h-4 w-4" /> Back
            </Button>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outline" onClick={() => saveDraft({ stay: true })} disabled={saving} className="gap-1.5" data-testid="save-draft-btn">
                <Save className="h-4 w-4" /> {postRelease ? "Save Changes" : "Save Draft"}
              </Button>
              {step < STEPS.length ? (
                <Button onClick={next} className="gap-1" data-testid="form-next-btn">
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              ) : postRelease ? null : (
                <Button onClick={release} disabled={saving} className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="release-btn">
                  <Send className="h-4 w-4" /> Release
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <ReleaseCelebration
        open={!!released}
        srNumber={released?.sr_number}
        onClose={() => setReleased(null)}
        onViewSale={() => navigate(`/sales/${released.id}`)}
      />
    </div>
  );
}

function Field({ label, hint, children, className }) {
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
      {children}
      {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
    </div>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}


// Dropdown-backed selector for masters that also handles historical typed
// values. If the persisted `value` isn't in the active-options list (e.g.
// the master was renamed or deactivated after the sale was created), it's
// still rendered as a pinned "current value" option so the sale keeps its
// original state/district on subsequent saves.
function MasterSelect({ value, options, onChange, placeholder, testid, disabled }) {
  const opts = options || [];
  const inList = opts.some((o) => (o.name || "").toLowerCase() === (value || "").toLowerCase());
  return (
    <Select value={value || ""} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {value && !inList && (
          <SelectItem value={value} data-testid={`${testid}-current`}>
            {value} · current
          </SelectItem>
        )}
        {opts.map((o) => (
          <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>
        ))}
        {opts.length === 0 && !value && (
          <div className="px-3 py-2 text-xs text-slate-500">
            No entries. Ask Admin to add via Admin → Masters.
          </div>
        )}
      </SelectContent>
    </Select>
  );
}


function ModeToggle({ value, onChange, testid }) {
  const opts = [
    { v: "abs", label: "₹" },
    { v: "pct", label: "%" },
  ];

  return (
    <div className="inline-flex overflow-hidden rounded-md border border-slate-200" data-testid={testid}>
      {opts.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          data-testid={`${testid}-${o.v}`}
          className={cn(
            "px-2 py-1 text-xs font-semibold transition-colors",
            value === o.v
              ? "bg-primary text-white"
              : "bg-white text-slate-600 hover:bg-slate-50",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function AccessoriesEditor({ items, total, onAdd, onUpdate, onRemove, locked = false, lockMessage = "" }) {
  return (
    <div className="rounded-lg border border-border bg-slate-50/50 p-4" data-testid="accessories-editor">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-900">Accessories</div>
          <div className="text-xs text-slate-500">
            Add items being sold with the vehicle. The total auto-fills the Accessories amount in Payment.
          </div>
        </div>
        <Button
          size="sm"
          onClick={onAdd}
          disabled={locked}
          className="gap-1"
          data-testid="add-accessory-btn"
        >
          <Plus className="h-3.5 w-3.5" /> Add Item
        </Button>
      </div>
      {locked && lockMessage && (
        <div
          className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="accessories-locked-banner"
        >
          <span className="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold">!</span>
          <span>{lockMessage}</span>
        </div>
      )}
      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-white px-3 py-4 text-center text-xs text-slate-500">
          No accessories added.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((a, i) => (
            <div key={a._uid || `acc-${i}`} className="flex items-center gap-2" data-testid={`accessory-row-${i}`}>
              <Input
                placeholder="Item name (e.g. Helmet)"
                value={a.name}
                onChange={(e) => onUpdate(i, "name", e.target.value)}
                onBlur={(e) => {
                  const v = toTitleCase(e.target.value);
                  if (v !== a.name) onUpdate(i, "name", v);
                }}
                readOnly={locked}
                disabled={locked}
                className={cn(
                  "flex-1",
                  locked && "bg-slate-100 text-slate-500 cursor-not-allowed",
                )}
                data-testid={`accessory-name-${i}`}
              />
              <Input
                placeholder="Price"
                type="number"
                min="0"
                value={a.price}
                onChange={(e) => onUpdate(i, "price", e.target.value)}
                readOnly={locked}
                disabled={locked}
                className={cn(
                  "w-32 text-right font-mono-data",
                  locked && "bg-slate-100 text-slate-500 cursor-not-allowed",
                )}
                data-testid={`accessory-price-${i}`}
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => onRemove(i)}
                disabled={locked}
                className="h-9 w-9 text-slate-400 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                data-testid={`remove-accessory-${i}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <div className="flex items-center justify-between rounded-md bg-white px-3 py-2.5">
            <span className="text-sm font-semibold text-slate-700">Accessories Total</span>
            <span className="font-mono-data text-base font-semibold text-primary" data-testid="accessories-total">
              ₹{total.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function VehicleStep({ form, setField, setForm, masters, isLocked = () => false }) {
  const [enterVin, setEnterVin] = useState(!!(form.vehicle.vin || form.vehicle.engine_no));
  // Vehicle Model list — master-driven. Falls back to bundled constants for any
  // env that hasn't seeded the masters yet.
  const modelOptions = (masters?.vehicle_model || []).length
    ? masters.vehicle_model.map((m) => m.name)
    : VEHICLE_MODELS;
  const vehicleLocked = isLocked("vehicle.model");
  const lockTitle = "Field locked after Sales Release";
  return (
    <div className="space-y-5">
      <Grid>
        <Field label="Model *">
          <Select value={form.vehicle.model} onValueChange={(v) => setField("vehicle", "model", v)} disabled={vehicleLocked}>
            <SelectTrigger data-testid="form-vehicle-model" title={vehicleLocked ? lockTitle : undefined}><SelectValue placeholder="Select model" /></SelectTrigger>
            <SelectContent>
              {modelOptions.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500">Add Vehicle Models in Admin → Masters</div>
              ) : modelOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Color Code *">
          <Input
            value={form.vehicle.color_code}
            onChange={(e) => setField("vehicle", "color_code", e.target.value)}
            placeholder="e.g. RACING BLUE"
            data-testid="form-vehicle-color"
            readOnly={isLocked("vehicle.color_code")}
            disabled={isLocked("vehicle.color_code")}
            title={isLocked("vehicle.color_code") ? lockTitle : undefined}
            className={isLocked("vehicle.color_code") ? "bg-slate-100 text-slate-500 cursor-not-allowed" : undefined}
          />
        </Field>
      </Grid>

      <div className="rounded-lg border border-border bg-slate-50 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Enter VIN &amp; Engine No?</div>
            <div className="text-xs text-slate-500">Optional now — PDI team can fill later.</div>
          </div>
          <Switch
            checked={enterVin}
            onCheckedChange={(v) => {
              setEnterVin(v);
              if (!v) setForm((f) => ({ ...f, vehicle: { ...f.vehicle, vin: "", engine_no: "" } }));
            }}
            data-testid="toggle-vin-engine"
            disabled={vehicleLocked}
          />
        </div>
        {enterVin && (
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field label="VIN" hint="Exactly 17 alphanumeric characters">
              <Input
                value={form.vehicle.vin}
                onChange={(e) => setField("vehicle", "vin", e.target.value.toUpperCase())}
                maxLength={17}
                readOnly={isLocked("vehicle.vin")}
                disabled={isLocked("vehicle.vin")}
                title={isLocked("vehicle.vin") ? lockTitle : undefined}
                className={isLocked("vehicle.vin") ? "bg-slate-100 text-slate-500 cursor-not-allowed" : undefined}
                data-testid="form-vehicle-vin"
              />
            </Field>
            <Field label="Engine Number" hint="Exactly 12 alphanumeric characters">
              <Input
                value={form.vehicle.engine_no}
                onChange={(e) => setField("vehicle", "engine_no", e.target.value.toUpperCase())}
                maxLength={12}
                readOnly={isLocked("vehicle.engine_no")}
                disabled={isLocked("vehicle.engine_no")}
                title={isLocked("vehicle.engine_no") ? lockTitle : undefined}
                className={isLocked("vehicle.engine_no") ? "bg-slate-100 text-slate-500 cursor-not-allowed" : undefined}
                data-testid="form-vehicle-engine"
              />
            </Field>
          </div>
        )}
      </div>
    </div>
  );
}

// Net amount after discount. Used by both Sales Form and downstream views.
const applyDiscountFn = (amount, disc) => {
  const a = Number(amount) || 0;
  const v = Number(disc?.value) || 0;
  if (!v) return a;
  const cut = disc?.mode === "pct" ? a * v / 100 : v;
  return Math.max(0, a - cut);
};

function PaymentStep({ form, setField, setForm, totalPayment, customerPayable, isV2Form, v2Summary }) {
  // Hypothecation row only when finance with explicit Yes. Legacy sales (None) keep
  // showing the row to avoid hiding existing data.
  const isFinance = form.finance.buy_mode === "finance";
  const showHypothecation = isFinance && form.finance.hypothecation !== false;
  const vas = form.vas || {};
  // VAS opt-in. Legacy null → treat as opted (consistent with workflow engine).
  const ppOpted  = vas.pp_opted !== false;
  const amcOpted = vas.amc_opted !== false;
  const rsaOpted = vas.rsa_opted !== false;
  const rows = [
    { key: "exshowroom", label: "Ex-Showroom" },
    { key: "registration", label: "Registration" },
    ...(showHypothecation ? [{ key: "hypothecation", label: "Hypothecation" }] : []),
    { key: "insurance", label: "Insurance" },
    { key: "pp", label: "PP",   disabled: !ppOpted,  notOptedNote: "Protection Plus not opted" },
    { key: "amc", label: "AMC", disabled: !amcOpted, notOptedNote: "AMC not opted" },
    { key: "rsa", label: "RSA", disabled: !rsaOpted, notOptedNote: "RSA not opted" },
    // v2: Accessories are separated below the Gross line, not inline with the
    // gross rows. v1 keeps them inline for backwards compat.
    ...(isV2Form ? [] : [{ key: "accessories", label: "Accessories" }]),
    { key: "admin_incidental", label: "Admin & Incidental Charges" },
  ];

  // Auto-zero disabled VAS amounts so they don't sneak into the total.
  // NOTE (Feb-2026): We used to CLEAR payment.pp/amc/rsa on opt-out, which
  // caused data loss when the user toggled Yes→No→Yes (they'd lose the price
  // silently). We now keep the values intact — the row is hidden in the UI
  // and excluded from the gross calc below via the `r.disabled` flag.
  // Kept as a no-op so any legacy stale price with opted=false is IGNORED
  // in the gross calculation (see the reduce below).

  // Single bottom-of-table discount mutator. Stored at payment.discounts.total.
  const totalDisc = form.payment.discounts?.total || { value: "", mode: "abs" };
  const setTotalDiscount = (patch) =>
    setForm((f) => ({
      ...f,
      payment: {
        ...f.payment,
        discounts: { ...(f.payment.discounts || {}), total: { ...totalDisc, ...patch } },
      },
    }));

  const gross = rows.reduce((s, r) => s + (r.disabled ? 0 : (Number(form.payment[r.key]) || 0)), 0);
  const cut = applyDiscountFn(gross, totalDisc) < gross ? gross - applyDiscountFn(gross, totalDisc) : 0;

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
          On-Road Price Breakup
        </div>
        <div className="overflow-hidden rounded-md border border-border">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-slate-50 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-2">Component</th><th className="px-4 py-2 w-48">Amount (₹)</th></tr>
            </thead>
            <tbody className="divide-y divide-border bg-white">
              {rows.map((r) => (
                <tr key={r.key}>
                  <td className="px-4 py-2 text-slate-700">
                    {r.label}
                    {r.key === "accessories" && (
                      <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                        auto · from Step 8
                      </span>
                    )}
                    {r.disabled && (
                      <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                        {r.notOptedNote}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-1.5">
                    <Input
                      type="number" min="0"
                      value={r.disabled ? "" : form.payment[r.key]}
                      onChange={(e) => setField("payment", r.key, e.target.value)}
                      readOnly={r.key === "accessories" || r.disabled}
                      disabled={r.disabled}
                      placeholder={r.disabled ? "Not opted" : ""}
                      data-testid={`form-pay-${r.key}`}
                      className={cn(
                        "h-8 text-right font-mono-data",
                        (r.key === "accessories" || r.disabled) && "bg-slate-50 text-slate-400 cursor-not-allowed",
                      )}
                    />
                  </td>
                </tr>
              ))}
              {/* v1 legacy sales keep the pre-Gross Discount row so old data
                   still renders the way it always has. v2 sales move Discount
                   below Net On-Road, above DDA. */}
              {!isV2Form && (
                <tr className="bg-slate-50/60">
                  <td className="px-4 py-2 text-slate-700">
                    Discount
                    <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                      {totalDisc.mode === "pct" ? "% of gross" : "flat ₹"}
                      {cut > 0 ? ` · −₹${cut.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : ""}
                    </span>
                  </td>
                  <td className="px-4 py-1.5">
                    <div className="flex items-center gap-1">
                      <Input
                        type="number" min="0" placeholder="0"
                        value={totalDisc.value || ""}
                        onChange={(e) => setTotalDiscount({ value: e.target.value })}
                        className="h-8 text-right font-mono-data"
                        data-testid="form-pay-discount-total"
                        disabled={!gross}
                      />
                      <ModeToggle
                        value={totalDisc.mode || "abs"}
                        onChange={(v) => setTotalDiscount({ mode: v })}
                        testid="form-pay-discount-mode"
                      />
                    </div>
                  </td>
                </tr>
              )}
              <tr className="bg-slate-50">
                <td className="px-4 py-2.5 text-sm font-semibold text-slate-900">
                  {isV2Form ? "Gross Amount" : "Total"}
                </td>
                <td className="px-4 py-2.5 text-right font-mono-data text-base font-semibold text-slate-900" data-testid="payment-total">
                  ₹{(isV2Form ? v2Summary.gross : totalPayment).toLocaleString("en-IN")}
                </td>
              </tr>
              {isV2Form && (
                <>
                  <tr>
                    <td className="px-4 py-2 text-slate-700">Accessories
                      <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                        auto · from Step 8
                      </span>
                    </td>
                    <td className="px-4 py-1.5">
                      <Input
                        type="number" value={form.payment.accessories} readOnly disabled
                        className="h-8 text-right font-mono-data bg-slate-50 text-slate-400 cursor-not-allowed"
                        data-testid="form-pay-accessories"
                      />
                    </td>
                  </tr>
                  <tr className="bg-primary/5">
                    <td className="px-4 py-2.5 text-sm font-bold text-primary">Net On-Road Price</td>
                    <td className="px-4 py-2.5 text-right font-mono-data text-base font-bold text-primary" data-testid="payment-net-on-road">
                      ₹{v2Summary.netOnRoad.toLocaleString("en-IN")}
                    </td>
                  </tr>
                  {/* v2: Discount row lives below Net On-Road, above DDA — spec addendum */}
                  <tr>
                    <td className="px-4 py-2 text-slate-700">
                      Discount
                      <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                        {totalDisc.mode === "pct" ? "% of Net On-Road" : "flat ₹"}
                        {v2Summary.discount > 0 ? ` · −₹${v2Summary.discount.toLocaleString("en-IN", { maximumFractionDigits: 0 })}` : ""}
                      </span>
                    </td>
                    <td className="px-4 py-1.5">
                      <div className="flex items-center gap-1">
                        <Input
                          type="number" min="0" placeholder="0"
                          value={totalDisc.value || ""}
                          onChange={(e) => setTotalDiscount({ value: e.target.value })}
                          className="h-8 text-right font-mono-data"
                          data-testid="form-pay-discount-total"
                          disabled={!v2Summary.netOnRoad}
                        />
                        <ModeToggle
                          value={totalDisc.mode || "abs"}
                          onChange={(v) => setTotalDiscount({ mode: v })}
                          testid="form-pay-discount-mode"
                        />
                      </div>
                    </td>
                  </tr>
                  {isFinance && (
                    <tr>
                      <td className="px-4 py-2 text-slate-700">Dealer Disbursement Amount (DDA)
                        <span className="ml-2 text-[10px] font-medium uppercase tracking-wider text-slate-400">
                          amount the financier disburses to the dealer
                        </span>
                      </td>
                      <td className="px-4 py-1.5">
                        <Input
                          type="number" min="0" step="1"
                          value={form.finance.dealer_disbursement_amount ?? ""}
                          onChange={(e) => setField("finance", "dealer_disbursement_amount", e.target.value)}
                          className="h-8 text-right font-mono-data"
                          data-testid="form-pay-dda"
                          placeholder="e.g. 65000"
                        />
                      </td>
                    </tr>
                  )}
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2.5 text-sm font-semibold text-emerald-800">
                      To Receive from Customer
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono-data text-base font-bold text-emerald-800" data-testid="payment-receivable">
                      ₹{v2Summary.receivable.toLocaleString("en-IN")}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {isFinance && !isV2Form && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary">
            <Banknote className="h-4 w-4" /> Finance — Down Payment
          </div>
          <Grid>
            <Field label="Down Payment (₹)">
              <Input
                type="number" min="0"
                value={form.payment.down_payment}
                onChange={(e) => setField("payment", "down_payment", e.target.value)}
                data-testid="form-pay-dp"
                aria-invalid={Number(form.payment.down_payment) > totalPayment + 0.5}
              />
              {Number(form.payment.down_payment) > totalPayment + 0.5 && (
                <p className="mt-1 text-[11px] font-semibold text-rose-600" data-testid="dp-over-error">
                  Downpayment cannot exceed Final Vehicle Price (₹{totalPayment.toLocaleString("en-IN")})
                </p>
              )}
            </Field>
            <Field label="Accessories (₹)">
              <Input type="number" min="0" value={form.payment.dp_accessories}
                readOnly disabled
                className="bg-slate-50 cursor-not-allowed"
                data-testid="form-pay-dp-acc" />
              <p className="mt-1 text-[10px] text-slate-500">
                Auto-filled from Accessories step total
              </p>
            </Field>
          </Grid>
          <div className="mt-3 flex items-center justify-between rounded-md bg-white px-4 py-3 text-sm">
            <span className="font-semibold text-slate-700">To be received from Customer</span>
            <span className="font-mono-data text-base font-semibold text-primary" data-testid="customer-payable">
              ₹{customerPayable.toLocaleString("en-IN")}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Stepper({ step, setStep, postRelease }) {
  return (
    <ol className="flex flex-wrap items-center gap-1.5" data-testid="form-stepper">
      {STEPS.map((s) => {
        const Icon = s.icon;
        const completed = step > s.id;
        const active = step === s.id;
        return (
          <li
            key={s.id}
            onClick={() => (postRelease || step >= s.id) && setStep(s.id)}
            className={cn(
              "flex flex-1 min-w-[110px] items-center gap-2 rounded-md border px-2.5 py-2 transition-colors",
              active && "border-primary bg-primary/5",
              completed && "cursor-pointer border-emerald-200 bg-emerald-50",
              !active && !completed && "border-border bg-white text-slate-400",
            )}
            data-testid={`step-${s.id}`}
          >
            <span className={cn(
              "flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold",
              active && "bg-primary text-primary-foreground",
              completed && "bg-emerald-500 text-white",
              !active && !completed && "bg-slate-100 text-slate-500",
            )}>
              {completed ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
            </span>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Step {s.id}</div>
              <div className={cn("truncate text-xs font-medium", active ? "text-primary" : completed ? "text-emerald-700" : "text-slate-700")}>
                {s.name}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}


function VasOptRow({ label, testid, opted, onOptedChange, planId, onPlanChange, plans, locked = false, lockMessage = "" }) {
  const yesBtn = (val, txt) => (
    <button
      type="button"
      onClick={() => !locked && onOptedChange(val)}
      disabled={locked}
      data-testid={`vas-${testid}-opted-${val ? "yes" : "no"}`}
      className={cn(
        "flex-1 rounded-md border px-3 py-2 text-sm font-semibold transition-all",
        opted === val
          ? val
            ? "border-emerald-500 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-200"
            : "border-rose-500 bg-rose-50 text-rose-800 ring-2 ring-rose-200"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300",
        locked && "cursor-not-allowed opacity-60 hover:border-slate-200",
      )}
    >
      {txt}
    </button>
  );
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`vas-row-${testid}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="font-display text-sm font-semibold text-slate-900">{label}</div>
          <div className="text-[11px] text-slate-500">
            Customer opted in for {label}?
          </div>
        </div>
        <div className="flex w-full max-w-xs gap-2">
          {yesBtn(true, "Yes")}
          {yesBtn(false, "No")}
        </div>
      </div>
      {locked && lockMessage && (
        <div
          className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid={`vas-${testid}-locked-banner`}
        >
          <span className="mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-amber-200 text-[10px] font-bold">!</span>
          <span>{lockMessage}</span>
        </div>
      )}
      {opted === true && (
        <div className="mt-3">
          <Label className="text-[11px] font-semibold text-slate-700">Select Plan *</Label>
          <Select value={planId || ""} onValueChange={onPlanChange} disabled={locked}>
            <SelectTrigger className="mt-1" data-testid={`form-vas-${testid}`}>
              <SelectValue placeholder="Select plan" />
            </SelectTrigger>
            <SelectContent>
              {plans.length === 0 ? (
                <div className="px-3 py-2 text-xs text-slate-500">Add plans in Admin → Masters</div>
              ) : plans.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name} {m.price != null && `· ₹${m.price}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {opted === false && (
        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
          Customer Not Opted · no {label} task will be generated for this sale.
        </div>
      )}
    </div>
  );
}
